import { useMemo, useState } from 'react';
import { goi, LoiApi, tien } from '../chung/api';
import { useHoiLai } from '../chung/hoi-lai';
import type { BaoCaoTonKho, DongTonKho } from '../chung/kieu';
import { DaiLoi, DaiTot, HopHoi, Khung, O } from './manh';

/**
 * Tồn kho — nhập hàng và kiểm kê.
 *
 * 🔴 Hai việc này CỐ Ý tách đôi, không gộp thành một ô "sửa tồn kho".
 *
 *    Nhập hàng chỉ nhận số DƯƠNG. Muốn giảm thì phải kiểm kê, vì kiểm kê buộc
 *    gõ **số đếm được thực tế** — có con số làm bằng chứng trong sổ. Một ô "sửa
 *    tồn kho" cho phép gõ đại một số rồi lưu, và sáu tháng sau không ai biết
 *    hôm đó thật sự còn bao nhiêu.
 */
export function MucTonKho() {
  const [q, datQ] = useState('');
  const [chiHet, datChiHet] = useState(false);
  const [viec, datViec] = useState<{ sp: DongTonKho; kieu: 'nhap' | 'kiem' } | null>(null);
  const [tot, datTot] = useState<string | null>(null);

  const bc = useHoiLai<BaoCaoTonKho>('/api/bao-cao/ton-kho', 10_000);

  const ds = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (bc.duLieu?.danh_sach ?? []).filter((d) => {
      if (chiHet && d.con_ban > 0) return false;
      if (!t) return true;
      return (
        d.ten.toLowerCase().includes(t) ||
        d.sku.toLowerCase().includes(t) ||
        (d.nhom ?? '').toLowerCase().includes(t)
      );
    });
  }, [bc.duLieu, q, chiHet]);

  return (
    <Khung
      tieuDe="Tồn kho"
      phu={
        bc.duLieu
          ? `${bc.duLieu.tong_mat_hang} mặt hàng · ${bc.duLieu.tong_het_hang} hết hàng · giá trị theo GIÁ BÁN ${tien(bc.duLieu.tong_gia_tri_theo_gia_ban)}`
          : 'Đang tải…'
      }
    >
      <DaiLoi loi={bc.loi} />
      <DaiTot chu={tot} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          value={q}
          onChange={(e) => datQ(e.target.value)}
          placeholder="Lọc theo tên, mã hoặc nhóm"
          spellCheck={false}
          style={{ flex: 1 }}
        />
        <button
          onClick={() => datChiHet((c) => !c)}
          className={chiHet ? 'lom-nho' : undefined}
          style={{ whiteSpace: 'nowrap', color: chiHet ? 'var(--nhan)' : undefined }}
        >
          Chỉ hàng hết
        </button>
      </div>

      {!bc.duLieu && [0, 1, 2, 3].map((i) => <div key={i} className="xuong" style={{ height: 58, marginBottom: 12 }} />)}

      {bc.duLieu && ds.length === 0 && (
        <div style={{ padding: '40px 10px', textAlign: 'center', color: 'var(--chu-3)' }}>
          Không có mặt hàng nào khớp.
        </div>
      )}

      {ds.map((d, i) => (
        <div
          key={d.id}
          className="noi-nho truot-phai"
          style={{
            animationDelay: `${Math.min(i, 8) * 30}ms`,
            padding: '13px 18px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>{d.ten}</span>
              {d.con_ban <= 0 && <span className="the the-nhan">Hết hàng</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 4 }}>
              {d.sku}
              {d.nhom && ` · ${d.nhom}`} · {tien(d.gia)}/{d.don_vi}
            </div>
          </div>

          {/* Ba con số tách bạch: trên kệ · giỏ đang giữ · CÒN BÁN.
              Nhân viên nhìn `con_ban`, quản lý cần thấy cả ba mới hiểu vì sao. */}
          <div style={{ display: 'flex', gap: 20, flexShrink: 0, textAlign: 'right' }}>
            <SoNho nhan="Thực tế" so={d.tren_ke} />
            <SoNho nhan="Giỏ giữ" so={d.dang_giu} mau={d.dang_giu > 0 ? 'var(--cho)' : undefined} />
            <SoNho nhan="Tồn kho" so={d.con_ban} dam mau={d.con_ban <= 0 ? 'var(--nhan)' : undefined} />
          </div>

          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => datViec({ sp: d, kieu: 'nhap' })}
              style={{ padding: '9px 13px', fontSize: 13 }}
            >
              Nhập hàng
            </button>
            <button
              onClick={() => datViec({ sp: d, kieu: 'kiem' })}
              style={{ padding: '9px 13px', fontSize: 13 }}
            >
              Kiểm kê
            </button>
          </div>
        </div>
      ))}

      {viec && (
        <HopViec
          sp={viec.sp}
          kieu={viec.kieu}
          dong={() => datViec(null)}
          xong={(c) => {
            datViec(null);
            datTot(c);
            bc.taiLai();
          }}
        />
      )}
    </Khung>
  );
}

function SoNho({ nhan, so, dam, mau }: { nhan: string; so: number; dam?: boolean; mau?: string }) {
  return (
    <div style={{ minWidth: 58 }}>
      <div className="nhan-chu" style={{ fontSize: 10 }}>
        {nhan}
      </div>
      <div
        className="so"
        style={{ fontSize: dam ? 19 : 16, fontWeight: dam ? 800 : 600, color: mau, marginTop: 2 }}
      >
        {so}
      </div>
    </div>
  );
}

function HopViec({
  sp,
  kieu,
  dong,
  xong,
}: {
  sp: DongTonKho;
  kieu: 'nhap' | 'kiem';
  dong: () => void;
  xong: (chu: string) => void;
}) {
  const [so, datSo] = useState('');
  const [ghiChu, datGhiChu] = useState('');
  const [dangLam, datDangLam] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const n = Number(so.replace(/[.\s]/g, ''));
  const hopLe = so.trim() !== '' && Number.isInteger(n) && (kieu === 'nhap' ? n > 0 : n >= 0);
  const lech = kieu === 'kiem' && hopLe ? n - sp.tren_ke : 0;

  const lam = async () => {
    if (!hopLe || dangLam) return;
    datDangLam(true);
    datLoi(null);
    try {
      if (kieu === 'nhap') {
        await goi('/api/ton-kho/nhap', {
          method: 'POST',
          than: { san_pham_id: sp.id, so_luong: n, ...(ghiChu.trim() ? { ghi_chu: ghiChu.trim() } : {}) },
        });
        xong(`Đã nhập ${n} ${sp.don_vi} "${sp.ten}".`);
      } else {
        const kq = await goi<{ truoc: number; sau: number; lech: number; da_ghi_so: boolean }>(
          '/api/ton-kho/kiem-ke',
          { method: 'POST', than: { san_pham_id: sp.id, so_dem_duoc: n } },
        );
        xong(
          kq.da_ghi_so
            ? `Kiểm kê "${sp.ten}": ${kq.truoc} → ${kq.sau} (lệch ${kq.lech > 0 ? '+' : ''}${kq.lech}), đã ghi sổ.`
            : `Kiểm kê "${sp.ten}": khớp ${kq.sau}, không lệch nên không ghi bút toán.`,
        );
      }
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không làm được.');
      datDangLam(false);
    }
  };

  return (
    <HopHoi
      tieuDe={kieu === 'nhap' ? `Nhập hàng — ${sp.sku}` : `Kiểm kê — ${sp.sku}`}
      nutY={kieu === 'nhap' ? 'GHI NHẬP HÀNG' : 'GHI KIỂM KÊ'}
      dangLam={dangLam}
      dong={dong}
      dongY={() => void lam()}
      choPhep={hopLe}
    >
      <DaiLoi loi={loi} />
      <div style={{ marginBottom: 18 }}>
        <b>{sp.ten}</b> — trên kệ hiện <b className="so">{sp.tren_ke}</b> {sp.don_vi}
        {sp.dang_giu > 0 && (
          <>
            , trong đó <b className="so">{sp.dang_giu}</b> đang bị giỏ giữ
          </>
        )}
        .
      </div>

      {kieu === 'nhap' ? (
        <>
          <O nhan={`Nhập thêm bao nhiêu ${sp.don_vi}?`}>
            <input value={so} onChange={(e) => datSo(e.target.value)} inputMode="numeric" className="so" autoFocus />
          </O>
          <O nhan="Ghi chú (không bắt buộc)">
            <input value={ghiChu} onChange={(e) => datGhiChu(e.target.value)} placeholder="Nhận từ NCC ngày 28/08" />
          </O>
          <div style={{ fontSize: 12, color: 'var(--chu-3)', lineHeight: 1.6 }}>
            Chỉ nhận số dương. Muốn <b>giảm</b> tồn thì dùng <b>Kiểm kê</b> — để có con số đếm
            được thật làm bằng chứng, thay vì một phép trừ không ai kiểm lại được.
          </div>
        </>
      ) : (
        <>
          {/*
            🔴 Gõ SỐ ĐẾM ĐƯỢC, không gõ chênh lệch.
               Bắt người đang đứng đếm hàng làm phép trừ trong đầu thì sẽ có lúc
               trừ nhầm, và sổ sách sai mà không ai biết. Hệ thống tự tính lệch.
          */}
          <O nhan={`Đếm được bao nhiêu ${sp.don_vi} trên kệ?`}>
            <input value={so} onChange={(e) => datSo(e.target.value)} inputMode="numeric" className="so" autoFocus />
          </O>
          {hopLe && (
            <div className="lom-nho" style={{ padding: '14px 16px' }}>
              <div className="so" style={{ fontSize: 17 }}>
                {sp.tren_ke} <span style={{ color: 'var(--chu-3)' }}>→</span>{' '}
                <span className="tien">{n}</span>
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontWeight: 700,
                  color: lech === 0 ? 'var(--tot)' : lech > 0 ? 'var(--cho)' : 'var(--nhan)',
                }}
              >
                {lech === 0
                  ? 'Khớp sổ — sẽ không ghi bút toán nào.'
                  : `Lệch ${lech > 0 ? '+' : ''}${lech} ${sp.don_vi} — sẽ ghi vào sổ kho.`}
              </div>
            </div>
          )}
        </>
      )}
    </HopHoi>
  );
}
